import { useAuthStore } from '@/stores/auth';
import type { Axios, AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import type {
	CategoryLesson,
	CategoryLessonsResponse,
	CategoryMetadataResponse,
	CategoryMetadataResponseMultiple,
	CategoryTypeV2,
	ChangeShowcaseMode,
	DetailedClassInfo,
	EmailVerificationTokenInfo,
	ProjectStatus,
	StudentInfo,
	Timestamps,
	UserDetails,
} from '@/stores/user';
import type { CategoryLessonsQueryResponse, CategoryQueryResponse } from '@/stores/project-catalog';
import { DateTime } from 'luxon';
import {
	clearThirdPartyAccountLinkContext,
	consumeThirdPartyAccountLinkContext,
	type ThirdPartyAccountLinkContext,
} from '@/helpers/thirdPartyAccountLinkContext';
import { getFirstTouchAttribution } from '@/helpers/attribution';
import { currentDeviceType } from '@/helpers/deviceType';
import { SCHOOL_SEARCH_LIMIT, type KycDetails, type KycProfile, type SchoolSearchResponse } from '@/helpers/kycTypes';
import type { SearchLocation } from '@/helpers/schoolLocations';

export enum ErrorCode {
	StudentImportDuplicateLoginCode = 'STUDENT_IMPORT_DUPLICATE_LOGIN_CODE',
	UnknownError = 'UNKNOWN',
}

type ErrorModel = {
	code: ErrorCode;
	messages: string[];
};

export type GenericErrorModel = {
	code: string;
	messages: string[];
};
export type GenericResponseModel = GenericErrorModel;

type HttpErrorResult = {
	status: number;
	errorModel: ErrorModel;
};

export type SpecifiedClassGrade =
	| 'KINDERGARTEN'
	| 'GRADE_01'
	| 'GRADE_02'
	| 'GRADE_03'
	| 'GRADE_04'
	| 'GRADE_05'
	| 'GRADE_06'
	| 'GRADE_07'
	| 'GRADE_08'
	| 'GRADE_09'
	| 'GRADE_10'
	| 'GRADE_11'
	| 'GRADE_12';
export type ClassGrade = 'UNSPECIFIED' | SpecifiedClassGrade;

export type QweebiClassStatus = 'ARCHIVED' | 'ACTIVE';

type NewClassType = {
	id: string;
	name?: string;
	status?: QweebiClassStatus;
	grade?: ClassGrade;
};

export type PlanType = 'BASIC' | 'PRO' | 'PRO_TRIAL';

export type UserCreditUsage = {
	basic: number;
	bonus: number;
	burned: number;
	available: number;
};

type PlanDetails = {
	type: PlanType;
	plan: {
		code: string;
		displayName: string;
		maxStudents: number | null;
	};
	activationDate: string;
	expiryDate: string | null;
	creditUsage: UserCreditUsage;
	numberOfDistinctActiveStudents: number;
};

type TemporaryData = {
	signUpQueryParams: string | null;
};

type StudentClassImportResponse = {
	classId: string;
	students: {
		id: string;
		name: string;
	}[];
};

type EvaluationType = {
	skillId: string;
	rating: number;
};

export type VerifyUserEmailResponse = {
	userDetails: UserDetails;
};
export type VerifyUserEmailErrorReason =
	'INVALID_OR_EXPIRED_VERIFICATION_TOKEN' | 'VERIFICATION_MISMATCHED_USER' | 'OTHER';
export type VerifyUserEmailError = {
	reason: VerifyUserEmailErrorReason;
};

export type ResendVerificationEmailResponse = EmailVerificationTokenInfo;
export type ResendVerificationEmailError = {
	reason: 'USER_ALREADY_VERIFIED' | 'OTHER';
};

type VerifyCoTeacherInviteAction = 'finalized' | 'login' | 'signup';

export type VerifyCoTeacherInviteSuccess = {
	action: VerifyCoTeacherInviteAction;
};

export type VerifyCoTeacherInviteError =
	{ reason: 'TOKEN_INVALID' } | { reason: 'EMAIL_MISMATCH'; invitedEmail: string };

/**
 * External OAuth provider for social login and class imports.
 *
 * Used for:
 * - Teacher social login/signup ("Continue with Google", "Continue with Microsoft")
 * - Google Classroom class/student imports
 * - Student social login
 */
export type ExternalAccountProvider = 'GOOGLE' | 'MICROSOFT' | 'CLEVER' | 'CLASSLINK';

export type ClassLoginType = 'CLASSCODE' | ExternalAccountProvider;

export type InitiateThirdPartyAccountLinkResponse =
	| {
			status: 'LINKED' | 'UNVERIFIABLE';
	  }
	| {
			status: 'REQUIRES_REAUTHENTICATION' | 'UNLINKED';
			oauthUri: string;
	  };

export type InitiateThirdPartyAccountLinkError = {
	reason: 'UNSUPPORTED_ACCOUNT_LINK_PROVIDER' | 'UNSUPPORTED_ACCOUNT_LINK_INTENT' | 'OTHER';
};

export type FinalizeThirdPartyAccountLinkResponse = {
	provider: ExternalAccountProvider;
	intent: 'IMPORT_CLASSES' | 'SYNC_CLASS_CHANGE' | 'TEACHER_LOGIN' | 'STUDENT_LOGIN';
	extra: Record<string, unknown>;
	validUntil: DateTime;
};

export type PendingEmailVerification = {
	generatedAt: string;
	validity: string;
	expiresAt: string;
	email: string;
};
export type PendingAccountLinkVerificationResponse = {
	pendingEmailVerification: PendingEmailVerification;
};

export type ConfirmAccountLinkErrorReason = 'INVALID_OR_EXPIRED_TOKEN' | 'OTHER';
export type ConfirmAccountLinkResponse = { status: 'SUCCESS' };
export type ConfirmAccountLinkError = { reason: ConfirmAccountLinkErrorReason };

export type TeacherOAuthLoginResponse = {
	user: {
		id: string;
		email: string;
		type: string;
		name: string;
		profile: Record<string, unknown> | null;
		dashboardTemporaryData: Record<string, unknown> | null;
		plan: Record<string, unknown> | null;
		linkedAccounts: Record<string, unknown>[];
		signUpSource: string | null;
		emailVerificationStatus: string;
	};
	token: {
		accessToken: string;
		expiresAt: string;
	};
	// OAuth metadata - indicates if account was newly created or linked during this login
	isNewAccount?: boolean;
	accountLinked?: boolean;
};

/**
 * Error returned when teacher OAuth login fails.
 * - USER_NOT_FOUND: No account exists for this email (TEACHER_LOGIN intent only)
 * - INVALID_PROVIDER: The OAuth provider is not supported
 * - OTHER: Generic error
 */
export type TeacherOAuthLoginError = {
	reason:
		| 'USER_NOT_FOUND'
		| 'INVALID_PROVIDER'
		| 'INVALID_PROMO_CODE'
		| 'SOCIAL_EMAIL_MISSING'
		| 'STUDENT_ACCOUNT_CANNOT_ACCESS_DASHBOARD'
		| 'OTHER';
	/** The email address from the OAuth provider (included when reason is USER_NOT_FOUND) */
	email?: string;
};
export type FinalizeThirdPartyAccountLinkError = {
	reason:
		'ACCOUNT_LINK_ERROR' | 'ACCOUNT_LINK_UNLINK_EXISTING_ACCOUNT' | 'INSUFFICIENT_ACCOUNT_LINK_PERMISSIONS' | 'OTHER';
	context: ThirdPartyAccountLinkContext | null;
};

export type UnlinkThirdPartyAccountError = {
	reason: 'INVALID_PROVIDER' | 'INVALID_ACCOUNT' | 'OTHER';
};

type ClassImportClassInfoApi = {
	id: string;
	name: string;
	importStatus: 'NOT_IMPORTED' | 'IMPORTED' | 'ARCHIVED';
	importDate: string | null;
};
export type ClassImportClassInfo =
	| {
			id: string;
			name: string;
			importStatus: 'NOT_IMPORTED';
			importDate: null;
	  }
	| {
			id: string;
			name: string;
			importStatus: 'IMPORTED';
			importDate: DateTime;
	  }
	| {
			id: string;
			name: string;
			importStatus: 'ARCHIVED';
			importDate: DateTime | null;
	  };
const convertClassImportClassInfoApiToClassImportClassInfo = (obj: ClassImportClassInfoApi): ClassImportClassInfo => {
	if (obj.importStatus === 'IMPORTED') {
		return {
			id: obj.id,
			name: obj.name,
			importStatus: obj.importStatus,
			importDate: DateTime.fromISO(obj.importDate!),
		};
	} else if (obj.importStatus === 'NOT_IMPORTED') {
		return {
			id: obj.id,
			name: obj.name,
			importStatus: obj.importStatus,
			importDate: null,
		};
	} else {
		return {
			id: obj.id,
			name: obj.name,
			importStatus: obj.importStatus,
			importDate: obj.importDate === null ? null : DateTime.fromISO(obj.importDate),
		};
	}
};
export type ListClassesFromLinkedAccountResponse = {
	provider: ExternalAccountProvider;
	availableClasses: ClassImportClassInfo[];
};
export type ListClassesFromLinkedAccountError = {
	reason:
		| 'UNSUPPORTED_PROVIDER'
		| 'NO_LINKED_ACCOUNT'
		| 'INVALID_CREDENTIALS'
		| 'PROVIDER_NOT_PROVISIONED'
		| 'PROVIDER_UNAVAILABLE'
		| 'OTHER';
};

export type ClassImportResult =
	| {
			classInfo: ClassImportClassInfo;
			status: 'SUCCESS';
			errorCode: null;
	  }
	| {
			classInfo: ClassImportClassInfo;
			status: 'FAILURE';
			errorCode: 'SERVER_ERROR' | 'IMPORT_ERROR';
	  };
export type ImportClassesFromLinkedAccountResponse = {
	provider: ExternalAccountProvider;
	results: ClassImportResult[];
};
export type ImportClassesFromLinkedAccountError = {
	reason:
		| 'UNSUPPORTED_PROVIDER'
		| 'NO_LINKED_ACCOUNT'
		| 'INVALID_CREDENTIALS'
		| 'PROVIDER_NOT_PROVISIONED'
		| 'ALREADY_IMPORTED'
		| 'OTHER';
};

export type SyncChangesForLinkedClassResponse = {
	classNameUpdated: boolean;
	studentsAdded: number;
	studentsArchived: number;
	studentsReactivated: number;
};
export type SyncChangesForLinkedAccountError = {
	reason:
		| 'INVALID_SOURCE'
		| 'AUTH_FAILURE'
		| 'INVALID_CREDENTIALS'
		| 'PROVIDER_NOT_PROVISIONED'
		| 'INVALID_CLASS'
		| 'CLASS_ARCHIVED'
		| 'OTHER';
};

export type UpdateClassDetailsError = {
	reason: 'FORBIDDEN' | 'OTHER';
};

export type FetchClassesResponse = {
	classes: DetailedClassInfo[];
};
export type FetchClassesError = {
	reason: 'OTHER';
};

export type FetchClassStudentsResponse = {
	students: StudentInfo[];
};
export type FetchClassStudentsError = {
	reason: 'OTHER';
};

export type ProjectAssignabilityStatus = 'ASSIGNABLE' | 'LICENSE_REQUIRED';

export type FetchProjectAssignabilityResponse = {
	assignability: ProjectAssignabilityStatus;
	creditsBurnedOnAssignment: number;
};

export type FetchProjectAssignabilityError = {
	reason: 'OTHER';
};

export type ChallengeStatus = 'NOT_STARTED' | 'STARTED' | 'COMPLETED' | 'SUBMITTED';

export type StudentChallengeStatus = {
	challengeId: string;
	status: ChallengeStatus;
};

export type ChallengeStatusStudent = {
	id: string;
	name: string;
	timestamps: Timestamps;
	challengeStatuses: StudentChallengeStatus[];
};

export type FetchChallengeStatusesResponse = {
	category: CategoryTypeV2;
	classId: string;
	students: ChallengeStatusStudent[];
};

export type FetchChallengeStatusesError = {
	reason: 'FORBIDDEN' | 'NOT_FOUND' | 'OTHER';
};

export type LogoutResponseSuccess = {
	status: 'SUCCESS';
};
export type LogoutResponseFailure = {
	status: 'FAILURE';
	reason: 'OTHER';
};

export type InviteColleagueSkipReason = 'INVALID_EMAIL' | 'DISPOSABLE_DOMAIN' | 'ALREADY_REGISTERED';

export type InviteColleagueResponse = {
	sent: string[];
	skipped: { email: string; reason: InviteColleagueSkipReason }[];
	failed: { email: string; reason: string }[];
};
export type InviteColleagueError = {
	reason: 'OTHER';
};

export type TeacherReferralLinkResponse = {
	link: string;
};
export type TeacherReferralLinkError = {
	reason: 'OTHER';
};

export type ResolveShortLinkResponse = {
	referralToken: string;
};
export type ResolveShortLinkError = {
	reason: 'SHORT_LINK_INVALID' | 'OTHER';
};

export type ClassTeacher =
	| {
			id: string;
			name: string;
			email: string;
			status: 'JOINED';
	  }
	| {
			id: string;
			name: string;
			email: string;
			status: 'PENDING';
	  }
	| {
			id: string;
			name: string;
			email: string;
			status: 'DECLINED';
	  }
	| {
			email: string;
			status: 'INVITED';
	  };

export type ClassOwner = {
	id: string;
	name: string;
	email: string;
};

export type ClassTeachersResponse = {
	owner: ClassOwner;
	coTeachers: ClassTeacher[];
};

export type InviteCoTeacherReason = 'INVALID_EMAIL' | 'ALREADY_COTEACHER' | 'OWNER_EMAIL' | 'OTHER';

export type InviteCoTeacherError = {
	reason: InviteCoTeacherReason;
};

export type ClassTeachersError = {
	reason: 'OTHER';
};

export type CoTeacherInvitation = {
	classId: string;
	className: string;
	grade: ClassGrade;
	numberOfStudents: number;
	classCode: string;
	source: 'INTERNAL' | 'EXTERNAL';
	externalInfo: {
		externalId: string;
		externalOwnerId: string;
		provider: ExternalAccountProvider;
	} | null;
	owner: ClassOwner;
	invitedAt: string;
};

export type CoTeacherInvitationsResponse = {
	invitations: CoTeacherInvitation[];
};

export type CoTeacherInviteActionError = {
	reason: 'NOT_FOUND' | 'OTHER';
};

export type SchoolSearchError = {
	reason: 'RATE_LIMITED' | 'CANCELLED' | 'OTHER';
};

export type UpdateProfileResponseSuccess = {
	status: 'SUCCESS';
	user: UserDetails;
};
export type UpdateProfileResponseFailure = {
	status: 'FAILURE';
	reason: 'OTHER';
};

export const extractEmailFromJwt = (token: string): string => {
	try {
		const payload = JSON.parse(atob(token.split('.')[1])) as { email?: string };
		return payload.email ?? 'another account';
	} catch {
		return 'another account';
	}
};

class ApiClient {
	private readonly authStore = useAuthStore();
	private readonly http: Axios;

	constructor() {
		const apiEndpoint = import.meta.env.VITE_QWEEBI_API_ENDPOINT;
		this.http = axios.create({
			baseURL: apiEndpoint,
			withCredentials: true,
		});

		this.http.interceptors.request.use((config) => {
			const accessToken = this.authStore.credentials?.accessToken ?? null;

			if (accessToken !== null && accessToken.length > 0) {
				config.headers['Authorization'] = `Bearer ${accessToken}`;
			}

			const isRunningInLocalDevelopment = import.meta.env.VITE_LOCAL_DEV;
			if (isRunningInLocalDevelopment === 'true') {
				config.headers['X-Local-Development'] = 'true';
			}

			return config;
		});

		// Add response interceptor to handle 401 errors
		this.http.interceptors.response.use(
			(response) => response,
			(error: AxiosError) => {
				const responseStatus = error.response?.status;
				if (responseStatus === 401) {
					this.authStore.showInvalidAccessTokenModal();
				} else if (!!responseStatus && responseStatus >= 500 && responseStatus < 600) {
					this.authStore.showInvalidAccessTokenModal();
				}
				return Promise.reject(error as Error);
			},
		);
	}

	login(email: string, password: string): Promise<AxiosResponse> {
		const requestBody = {
			email: email,
			password: password,
		};
		return this.http.post('v1.2/auth/users/login', requestBody);
	}

	requestPassword(email: string): Promise<AxiosResponse> {
		return this.http.post('v1.2/auth/users/requestPasswordReset', { email: email });
	}

	resetPassword(resetObj: { password: string; passwordConfirmation: string; token: string }): Promise<AxiosResponse> {
		return this.http.post('v1.2/auth/users/resetUserPassword', resetObj);
	}

	checkPromoCode(promoCode: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/auth/users/signup/checkPromoCode?promoCode=${encodeURIComponent(promoCode)}`);
	}

	signUp(
		email: string,
		password: string,
		passwordConfirmation: string,
		name: string,
		promoCode: string,
		signUpSource: string,
		referralToken?: string,
	): Promise<AxiosResponse> {
		const attribution = getFirstTouchAttribution();
		return this.http.post('v1.2/auth/users/signup', {
			email: email,
			password: password,
			name: name,
			passwordConfirmation: passwordConfirmation,
			promoCode: promoCode,
			signUpSource: signUpSource,
			referralToken: referralToken,
			acquisitionReferrer: attribution?.referrer,
			acquisitionLandingUrl: attribution?.landingUrl,
			deviceType: currentDeviceType(),
		});
	}

	async verifyCoTeacherInvite(token: string): Promise<VerifyCoTeacherInviteSuccess | VerifyCoTeacherInviteError> {
		try {
			const response = await this.http.get(
				`v1.2/auth/invitations/co-teacher/verify?token=${encodeURIComponent(token)}`,
			);
			return response.data as VerifyCoTeacherInviteSuccess;
		} catch (error) {
			const code = (error as AxiosError<GenericErrorModel | undefined>).response?.data?.code;
			if (code === 'COTEACHER_INVITE_EMAIL_MISMATCH') {
				const invitedEmail = extractEmailFromJwt(token);
				return { reason: 'EMAIL_MISMATCH', invitedEmail: invitedEmail };
			}
			return { reason: 'TOKEN_INVALID' };
		}
	}

	async logout(): Promise<LogoutResponseSuccess | LogoutResponseFailure> {
		try {
			await this.http.post('v1.2/auth/users/logout', {});
			return { status: 'SUCCESS' };
		} catch (error: unknown) {
			if (error instanceof Error && 'response' in error) {
				const axiosError = error as AxiosError;
				console.error('Error logging user out:', axiosError.toJSON());
				return {
					status: 'FAILURE',
					reason: 'OTHER',
				};
			} else {
				return {
					status: 'FAILURE',
					reason: 'OTHER',
				};
			}
		}
	}

	self(): Promise<AxiosResponse> {
		return this.http.get('v1.2/auth/users/me');
	}

	async updateProfile(
		updatedName: string,
		updatedKycDetails: KycDetails,
	): Promise<UpdateProfileResponseSuccess | UpdateProfileResponseFailure> {
		try {
			const axiosResponse = await this.http.put('v1.2/auth/users/me', {
				displayName: updatedName,
				kycProfile: updatedKycDetails,
			});
			return {
				status: 'SUCCESS',
				user: axiosResponse.data as UserDetails,
			};
		} catch (error: unknown) {
			if (error instanceof Error && 'response' in error) {
				const axiosError = error as AxiosError;
				console.error('Error updating profile:', axiosError.toJSON());
				return {
					status: 'FAILURE',
					reason: 'OTHER',
				};
			} else {
				return {
					status: 'FAILURE',
					reason: 'OTHER',
				};
			}
		}
	}

	async verifyUserEmail(verificationToken: string): Promise<VerifyUserEmailResponse | VerifyUserEmailError> {
		try {
			const axiosResponse = await this.http.post('v1.2/auth/users/verifyEmail', { token: verificationToken });
			return {
				userDetails: axiosResponse.data as UserDetails,
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;

				switch (responseData.code) {
					case 'INVALID_OR_EXPIRED_VERIFICATION_TOKEN': {
						return { reason: 'INVALID_OR_EXPIRED_VERIFICATION_TOKEN' };
					}
					case 'VERIFICATION_MISMATCHED_USER': {
						return { reason: 'VERIFICATION_MISMATCHED_USER' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async resendVerificationEmail(): Promise<ResendVerificationEmailResponse | ResendVerificationEmailError> {
		try {
			const axiosResponse = await this.http.post('v1.2/auth/users/resendVerificationEmail');
			return axiosResponse.data as ResendVerificationEmailResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;

				switch (responseData.code) {
					case 'VERIFICATION_USER_ALREADY_VERIFIED': {
						return { reason: 'USER_ALREADY_VERIFIED' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async initiateTeacherOAuthLogin(
		provider: ExternalAccountProvider = 'GOOGLE',
		signUpSource: string,
		intent: 'TEACHER_LOGIN' | 'TEACHER_SIGNUP' = 'TEACHER_LOGIN',
		promoCode?: string,
	): Promise<{ oauthUri: string } | TeacherOAuthLoginError> {
		clearThirdPartyAccountLinkContext();
		try {
			const extra: {
				signUpSource?: string;
				promoCode?: string;
				acquisitionReferrer?: string;
				acquisitionLandingUrl?: string;
			} = {};
			if (signUpSource) {
				extra.signUpSource = signUpSource;
			}
			if (promoCode && promoCode.length > 0) {
				extra.promoCode = promoCode;
			}
			const attribution = getFirstTouchAttribution();
			if (attribution !== null) {
				extra.acquisitionReferrer = attribution.referrer;
				extra.acquisitionLandingUrl = attribution.landingUrl;
			}
			const response = await this.http.post('v1.2/auth/users/accountLinks/initiate', {
				provider: provider,
				intent: intent,
				extra: extra,
			});

			const initiateResponse = response.data as InitiateThirdPartyAccountLinkResponse;
			if ('oauthUri' in initiateResponse && initiateResponse.oauthUri) {
				return { oauthUri: initiateResponse.oauthUri };
			} else {
				return { reason: 'OTHER' };
			}
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'UNSUPPORTED_ACCOUNT_LINK_PROVIDER': {
						return { reason: 'INVALID_PROVIDER' };
					}
					case 'SIGNUP_INVALID_PROMO_CODE': {
						return { reason: 'INVALID_PROMO_CODE' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async initiateThirdPartyAccountLink(
		provider: ExternalAccountProvider,
		intent: 'IMPORT_CLASSES' | 'SYNC_CLASS_CHANGE' | 'TEACHER_LOGIN',
		extra: Record<string, unknown> = {},
	): Promise<InitiateThirdPartyAccountLinkResponse | InitiateThirdPartyAccountLinkError> {
		clearThirdPartyAccountLinkContext();
		try {
			const response = await this.http.post('v1.2/auth/users/accountLinks/initiate', {
				provider: provider,
				intent: intent,
				extra: extra,
			});
			return response.data as InitiateThirdPartyAccountLinkResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'UNSUPPORTED_ACCOUNT_LINK_PROVIDER': {
						return { reason: 'UNSUPPORTED_ACCOUNT_LINK_PROVIDER' };
					}
					case 'UNSUPPORTED_ACCOUNT_LINK_INTENT': {
						return { reason: 'UNSUPPORTED_ACCOUNT_LINK_INTENT' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async finalizeThirdPartyAccountLink(
		oauthUri: string,
	): Promise<
		| FinalizeThirdPartyAccountLinkResponse
		| FinalizeThirdPartyAccountLinkError
		| TeacherOAuthLoginResponse
		| TeacherOAuthLoginError
		| PendingAccountLinkVerificationResponse
	> {
		const storedContext = consumeThirdPartyAccountLinkContext(oauthUri);

		try {
			const axiosResponse = await this.http.post('v1.2/auth/users/accountLinks/authorize', {
				oauthResponseUri: oauthUri,
				deviceType: currentDeviceType(),
			});

			// Check if this is a teacher login response (has user and token)
			if ('user' in axiosResponse.data && 'token' in axiosResponse.data) {
				// This is a teacher login response
				return axiosResponse.data as TeacherOAuthLoginResponse;
			}

			// Check if this is a pending email verification response
			if ('pendingEmailVerification' in axiosResponse.data) {
				return axiosResponse.data as PendingAccountLinkVerificationResponse;
			}

			// Otherwise, it's a regular account link response
			const response = axiosResponse.data as {
				provider: ExternalAccountProvider;
				intent: 'IMPORT_CLASSES' | 'SYNC_CLASS_CHANGE' | 'TEACHER_LOGIN' | 'STUDENT_LOGIN';
				extra: Record<string, unknown>;
				validUntil: string;
			};
			return {
				provider: response.provider,
				intent: response.intent,
				extra: response.extra,
				validUntil: DateTime.fromISO(response.validUntil),
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400 || axiosError.response!.status === 403) {
				const responseData = axiosError.response!.data as GenericErrorModel & {
					data?: ThirdPartyAccountLinkContext | { email: string };
				};

				switch (responseData.code) {
					case 'LOGIN_USER_NOT_FOUND': {
						// OAuth login error - user doesn't exist
						const errorData = responseData.data as { email: string } | undefined;
						return { reason: 'USER_NOT_FOUND', email: errorData?.email };
					}
					case 'OAUTH_SOCIAL_EMAIL_MISSING': {
						return { reason: 'SOCIAL_EMAIL_MISSING' };
					}
					case 'OAUTH_STUDENT_ACCOUNT_CANNOT_ACCESS_DASHBOARD': {
						return { reason: 'STUDENT_ACCOUNT_CANNOT_ACCESS_DASHBOARD' };
					}
					case 'ACCOUNT_LINK_ERROR': {
						return {
							reason: 'ACCOUNT_LINK_ERROR',
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: runtime data may be null
							context: (responseData.data as ThirdPartyAccountLinkContext) ?? storedContext,
						};
					}
					case 'ACCOUNT_LINK_UNLINK_EXISTING_ACCOUNT': {
						return {
							reason: 'ACCOUNT_LINK_UNLINK_EXISTING_ACCOUNT',
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: runtime data may be null
							context: (responseData.data as ThirdPartyAccountLinkContext) ?? storedContext,
						};
					}
					case 'INSUFFICIENT_ACCOUNT_LINK_PERMISSIONS': {
						return {
							reason: 'INSUFFICIENT_ACCOUNT_LINK_PERMISSIONS',
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: runtime data may be null
							context: (responseData.data as ThirdPartyAccountLinkContext) ?? storedContext,
						};
					}
					default: {
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: runtime data may be null
						return { reason: 'OTHER', context: (responseData.data as ThirdPartyAccountLinkContext) ?? storedContext };
					}
				}
			} else {
				return { reason: 'OTHER', context: storedContext };
			}
		}
	}

	async confirmAccountLink(token: string): Promise<ConfirmAccountLinkResponse | ConfirmAccountLinkError> {
		try {
			await this.http.post('v1.2/auth/users/accountLinks/confirmLink', { token: token });
			return { status: 'SUCCESS' };
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'ACCOUNT_LINK_CONFIRM_TOKEN_INVALID': {
						return { reason: 'INVALID_OR_EXPIRED_TOKEN' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async unlinkThirdPartyAccount(
		provider: ExternalAccountProvider,
	): Promise<GenericResponseModel | UnlinkThirdPartyAccountError> {
		try {
			const axiosResponse = await this.http.post('v1.2/auth/users/accountLinks/unlink', {
				provider: provider,
			});
			return axiosResponse.data as GenericResponseModel;
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'ACCOUNT_UNLINK_INVALID_PROVIDER': {
						return { reason: 'INVALID_PROVIDER' };
					}
					case 'ACCOUNT_UNLINK_INVALID_ACCOUNT': {
						return { reason: 'INVALID_ACCOUNT' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async listClassesAvailableOnThirdPartyLinkedPlatform(
		providerName: ExternalAccountProvider,
	): Promise<ListClassesFromLinkedAccountResponse | ListClassesFromLinkedAccountError> {
		try {
			const axiosResponse = await this.http.get(`v1.2/classes/listFromLinkedAccount/${providerName}`);
			const response = axiosResponse.data as {
				provider: ExternalAccountProvider;
				availableClasses: {
					id: string;
					name: string;
					importStatus: 'NOT_IMPORTED' | 'IMPORTED' | 'ARCHIVED';
					importDate: string | null;
				}[];
			};
			return {
				provider: response.provider,
				availableClasses: response.availableClasses.map(convertClassImportClassInfoApiToClassImportClassInfo),
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400 || axiosError.response!.status === 500) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'CLASS_IMPORT_UNSUPPORTED_PROVIDER': {
						return { reason: 'UNSUPPORTED_PROVIDER' };
					}
					case 'CLASS_IMPORT_NO_LINKED_ACCOUNT': {
						return { reason: 'NO_LINKED_ACCOUNT' };
					}
					case 'CLASS_IMPORT_INVALID_CREDENTIALS': {
						return { reason: 'INVALID_CREDENTIALS' };
					}
					case 'CLASS_IMPORT_PROVIDER_NOT_PROVISIONED': {
						return { reason: 'PROVIDER_NOT_PROVISIONED' };
					}
					case 'CLASS_IMPORT_PROVIDER_UNAVAILABLE': {
						return { reason: 'PROVIDER_UNAVAILABLE' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async importClassesFromThirdPartyLinkedPlatform(
		providerName: ExternalAccountProvider,
		classesToImport: ClassImportClassInfo[],
	): Promise<ImportClassesFromLinkedAccountResponse | ImportClassesFromLinkedAccountError> {
		try {
			const axiosResponse = await this.http.post('v1.2/classes/importFromLinkedAccount', {
				provider: providerName,
				classesToImport: classesToImport.map((c) => {
					return {
						id: c.id,
						name: c.name,
						importStatus: c.importStatus,
						importDate: c.importDate === null ? null : c.importDate.toJSDate().toISOString(),
						internalId: null,
					};
				}),
			});
			const response = axiosResponse.data as {
				provider: ExternalAccountProvider;
				results: {
					classInfo: {
						id: string;
						name: string;
						importStatus: 'NOT_IMPORTED' | 'IMPORTED' | 'ARCHIVED';
						importDate: string | null;
					};
					status: 'SUCCESS' | 'FAILURE';
					errorCode: 'SERVER_ERROR' | 'IMPORT_ERROR';
				}[];
			};
			return {
				provider: response.provider,
				results: response.results.map((r) => {
					if (r.status === 'SUCCESS') {
						return {
							classInfo: convertClassImportClassInfoApiToClassImportClassInfo(r.classInfo),
							status: r.status,
							errorCode: null,
						};
					} else {
						return {
							classInfo: convertClassImportClassInfoApiToClassImportClassInfo(r.classInfo),
							status: r.status,
							errorCode: r.errorCode,
						};
					}
				}),
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'CLASS_IMPORT_UNSUPPORTED_PROVIDER': {
						return { reason: 'UNSUPPORTED_PROVIDER' };
					}
					case 'CLASS_IMPORT_NO_LINKED_ACCOUNT': {
						return { reason: 'NO_LINKED_ACCOUNT' };
					}
					case 'CLASS_IMPORT_INVALID_CREDENTIALS': {
						return { reason: 'INVALID_CREDENTIALS' };
					}
					case 'CLASS_IMPORT_PROVIDER_NOT_PROVISIONED': {
						return { reason: 'PROVIDER_NOT_PROVISIONED' };
					}
					case 'CLASS_IMPORT_ALREADY_IMPORTED': {
						return { reason: 'ALREADY_IMPORTED' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async syncClassroomChangesFromThirdPartyLinkedPlatform(
		classId: string,
	): Promise<SyncChangesForLinkedClassResponse | SyncChangesForLinkedAccountError> {
		try {
			const axiosResponse = await this.http.post(`v1.2/classes/${classId}/syncFromLinkedAccount`, {});
			return axiosResponse.data as SyncChangesForLinkedClassResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 400) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'CLASS_SYNC_INVALID_SOURCE': {
						return { reason: 'INVALID_SOURCE' };
					}
					case 'CLASS_SYNC_AUTH_FAILURE': {
						return { reason: 'AUTH_FAILURE' };
					}
					case 'CLASS_SYNC_INVALID_CREDENTIALS': {
						return { reason: 'INVALID_CREDENTIALS' };
					}
					case 'CLASS_SYNC_PROVIDER_NOT_PROVISIONED': {
						return { reason: 'PROVIDER_NOT_PROVISIONED' };
					}
					case 'CLASS_SYNC_INVALID_CLASS': {
						return { reason: 'INVALID_CLASS' };
					}
					case 'CLASS_SYNC_CLASS_ARCHIVED': {
						return { reason: 'CLASS_ARCHIVED' };
					}
					default: {
						return { reason: 'OTHER' };
					}
				}
			} else {
				return { reason: 'OTHER' };
			}
		}
	}

	async updateClassDetails(updatedClassDetails: NewClassType): Promise<DetailedClassInfo | UpdateClassDetailsError> {
		try {
			const axiosResponse = await this.http.post('v1.2/classes/update', updatedClassDetails);
			return axiosResponse.data as DetailedClassInfo;
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response!.status === 403) {
				const responseData = axiosError.response!.data as GenericErrorModel;
				switch (responseData.code) {
					case 'FORBIDDEN': {
						console.error(
							'Updating class details was forbidden.',
							`UpdatedClassDetails: ${JSON.stringify(updatedClassDetails)}`,
						);
						return { reason: 'FORBIDDEN' };
					}
					default: {
						console.error(
							'Error updating class details.',
							`UpdatedClassDetails: ${JSON.stringify(updatedClassDetails)}`,
							`Error: ${String(axiosError.response!.data)}`,
						);
						return { reason: 'OTHER' };
					}
				}
			} else {
				console.error(
					'Error updating class details.',
					`UpdatedClassDetails: ${JSON.stringify(updatedClassDetails)}`,
					`Error: ${String(axiosError.response!.data)}`,
				);
				return { reason: 'OTHER' };
			}
		}
	}

	async fetchMyClasses(): Promise<FetchClassesResponse | FetchClassesError> {
		try {
			const axiosResponse = await this.http.get('v1.2/classes/mine');
			return axiosResponse.data as FetchClassesResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			console.error('Error fetching my classes list:', axiosError);
			return { reason: 'OTHER' };
		}
	}

	async fetchStudentsForClass(classId: string): Promise<FetchClassStudentsResponse | FetchClassStudentsError> {
		try {
			const axiosResponse = await this.http.get(`v1.2/classes/${classId}/students`);
			return axiosResponse.data as FetchClassStudentsResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			console.error('Error fetching my classes list:', axiosError);
			return { reason: 'OTHER' };
		}
	}

	async fetchProjectAssignability(
		projectId: string,
	): Promise<FetchProjectAssignabilityResponse | FetchProjectAssignabilityError> {
		try {
			const axiosResponse = await this.http.get(`v1.2/classes/assignments/${projectId}/assignability`);
			return axiosResponse.data as FetchProjectAssignabilityResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			console.error('Error fetching project assignability:', axiosError);
			return { reason: 'OTHER' };
		}
	}

	myClasses(): Promise<AxiosResponse> {
		return this.http.get(`v1.2/classes/mine`);
	}

	fetchClassesForProject(categoryId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/classes/assignmentStatus/${categoryId}`);
	}

	createAssignment(reqBody: {
		projectId: string;
		classes: { id: string }[];
		units?: { unitIdsToAdd: string[]; unitIdsToRemove: string[] };
	}): Promise<AxiosResponse> {
		return this.http.post(`v1.2/classes/assignments/create`, reqBody);
	}

	unassignProjectFromClass(assignmentId: string): Promise<AxiosResponse> {
		return this.http.delete(`v1.2/classes/assignments/${assignmentId}`);
	}

	fetchAssignmentsForClass(classId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/classes/${classId}/assignments`);
	}

	async fetchChallengeStatuses(
		classId: string,
		categoryId: string,
	): Promise<FetchChallengeStatusesResponse | FetchChallengeStatusesError> {
		try {
			const axiosResponse = await this.http.get(`v1.2/classes/${classId}/challengeStatuses/${categoryId}`);
			return axiosResponse.data as FetchChallengeStatusesResponse;
		} catch (error) {
			const axiosError = error as AxiosError;
			if (axiosError.response?.status === 403) {
				return { reason: 'FORBIDDEN' };
			}
			if (axiosError.response?.status === 404) {
				return { reason: 'NOT_FOUND' };
			}
			console.error('Error fetching challenge statuses:', axiosError);
			return { reason: 'OTHER' };
		}
	}

	createClass(className: string, classGrade: ClassGrade): Promise<DetailedClassInfo | HttpErrorResult> {
		const requestBody = {
			name: className,
			grade: classGrade,
		};

		return this.http
			.post('v1.2/classes', requestBody)
			.then(this.convertResponse<DetailedClassInfo>)
			.catch(this.convertError);
	}

	updateClass(newClassDetails: NewClassType): Promise<AxiosResponse> {
		return this.http.post(`v1.2/classes/update`, newClassDetails);
	}

	submitKyc(kycDetails: KycDetails): Promise<AxiosResponse> {
		return this.http.post(`v1.2/auth/users/kycProfile/mine`, kycDetails);
	}

	async searchSchools(
		searchTerm: string,
		searchWithMaps: boolean,
		location: SearchLocation,
		signal: AbortSignal | null,
	): Promise<SchoolSearchResponse | SchoolSearchError> {
		try {
			const response = await this.http.get('v1.2/schools/search', {
				params: {
					searchTerm: searchTerm,
					searchWithMaps: searchWithMaps,
					limit: SCHOOL_SEARCH_LIMIT,
					countryCode: location.countryCode ?? undefined,
					state: location.countryCode === null ? undefined : (location.state ?? undefined),
				},
				signal: signal ?? undefined,
			});
			return response.data as SchoolSearchResponse;
		} catch (error) {
			if (axios.isCancel(error)) {
				return { reason: 'CANCELLED' };
			}
			if (axios.isAxiosError(error) && error.response?.status === 429) {
				return { reason: 'RATE_LIMITED' };
			}
			return { reason: 'OTHER' };
		}
	}

	updateTemporaryData(data: TemporaryData): Promise<AxiosResponse> {
		return this.http.post(`v1.2/auth/users/dashboardTemporaryData/mine`, data);
	}

	studentsInClass(classId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/classes/${classId}/students`);
	}

	categories(applicableStatus: ProjectStatus[]): Promise<AxiosResponse> {
		const queryParams = new URLSearchParams(applicableStatus.map((s) => ['status', s]));
		return this.http.get(`v1.2/projects/categories?${queryParams}`, {
			params: queryParams,
		});
	}

	categoryDetails(id: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/projects/categories/${id}/details`);
	}

	categoryMetadata(id: string): Promise<CategoryMetadataResponse | HttpErrorResult> {
		return this.http
			.get(`v1.2/projects/categories/${id}/metadata`)
			.then(this.convertResponse<CategoryMetadataResponse>)
			.catch(this.convertError);
	}

	categoryMetadataMultiple(categoryIds: string[]): Promise<CategoryMetadataResponseMultiple | HttpErrorResult> {
		const queryParams = new URLSearchParams(categoryIds.map((c) => ['categoryIds', c]));
		return this.http
			.get(`v1.2/projects/categories/metadata?${queryParams}`)
			.then(this.convertResponse<CategoryMetadataResponseMultiple>)
			.catch(this.convertError);
	}

	queryProjectCategories(query: {
		categoryIds: string[];
		statuses: ProjectStatus[];
		includeLessons: boolean;
	}): Promise<CategoryQueryResponse | HttpErrorResult> {
		const queryParams = new URLSearchParams([
			...query.categoryIds.map((id) => ['categoryId', id]),
			...query.statuses.map((s) => ['categoryStatus', s]),
		]);
		queryParams.append('includeMetadata', 'true');
		queryParams.append('includeAssignability', 'true');
		queryParams.append('includeCurrentAssignmentStatus', 'true');
		queryParams.append('includeLessons', query.includeLessons.toString());
		return this.http
			.get(`v1.2/projects/categories/query?${queryParams}`)
			.then(this.convertResponse<CategoryQueryResponse>)
			.catch(this.convertError);
	}

	queryProjectCategoryLessons(categoryId: string): Promise<CategoryLessonsQueryResponse | HttpErrorResult> {
		const queryParams = new URLSearchParams([
			['categoryId', categoryId],
			['includeLessons', 'true'],
		]);
		return this.http
			.get(`v1.2/projects/categories/query?${queryParams}`)
			.then(this.convertResponse<CategoryLessonsQueryResponse>)
			.catch(this.convertError);
	}

	newLessons(categoryId?: string): Promise<CategoryLesson[] | HttpErrorResult> {
		const url = categoryId ? `v1.2/new-lessons/?categoryId=${categoryId}` : `v1.2/new-lessons/`;
		return this.http
			.get(url)
			.then(this.convertResponse<CategoryLessonsResponse>)
			.then((response) => response.lessons)
			.catch(this.convertError);
	}

	categoryProjects(categoryId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/projects/templates/byCategory/${categoryId}`);
	}

	evaluationCriteria(classId: string, lessonId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/assessments/evaluationCriteria/${classId}/${lessonId}`);
	}

	evaluateStudent(submissionId: string, evaluation: EvaluationType[]): Promise<AxiosResponse> {
		return this.http.post(`v1.2/assessments/submissions/evaluate`, {
			submissionId: submissionId,
			evaluation: evaluation,
		});
	}

	addComment(submissionId: string, content: string): Promise<AxiosResponse> {
		return this.http.post(`v1.2/submissions/comments`, { submissionId: submissionId, content: content });
	}

	requestResubmit(submissionId: string, comment: string): Promise<AxiosResponse> {
		return this.http.post(`v1.2/assessments/submissions/requestResubmission`, {
			submissionId: submissionId,
			comment: comment,
		});
	}

	submissionToFavorite(submissionId: string): Promise<AxiosResponse> {
		return this.http.post(`v1.2/submissions/bookmarks/add`, {
			submissionId: submissionId,
		});
	}

	removeSubFromFavorite(submissionId: string): Promise<AxiosResponse> {
		return this.http.post(`v1.2/submissions/bookmarks/remove`, {
			submissionId: submissionId,
		});
	}

	favoriteSubmissions(): Promise<AxiosResponse> {
		return this.http.get(`v1.2/submissions/bookmarks/mine`);
	}

	submissions(currentClassId: string, currentLessonId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/classes/${currentClassId}/submissionsForLesson/${currentLessonId}`);
	}

	submissionDetails(submissionId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/submissions/${submissionId}`);
	}

	importStudentsIntoClass(
		classId: string,
		studentsToImport: {
			name: string;
			loginCode: string;
		}[],
	): Promise<StudentClassImportResponse | HttpErrorResult> {
		const requestBody = {
			classId: classId,
			students: studentsToImport,
		};

		return this.http
			.post(`v1.2/classes/importStudents`, requestBody)
			.then(this.convertResponse<StudentClassImportResponse>)
			.catch(this.convertError);
	}

	deleteStudent(studentId: string): Promise<AxiosResponse> {
		return this.http.delete(`v1.2/classes/students/${studentId}`);
	}

	updateStudent(userDetails: { studentId: string; name: string; loginCode: string }): Promise<AxiosResponse> {
		return this.http.post(`v1.2/classes/updateStudentInfo`, userDetails);
	}

	showcases(): Promise<AxiosResponse> {
		return this.http.get(`v1.2/showcases/mine`);
	}

	showcase(id: string): Promise<AxiosResponse> {
		return this.http.get(`/v1.2/showcases/${id}/view`);
	}

	publicShowcase(id: string): Promise<AxiosResponse> {
		const apiEndpoint = import.meta.env.VITE_QWEEBI_API_ENDPOINT;
		return axios.get(`${apiEndpoint}/v1.2/showcases/${id}/public`);
	}

	createShowcase(showcase: {
		name: string;
		description: string;
		subtitle: string;
		attribution: string;
		submissions: string[];
	}): Promise<AxiosResponse> {
		return this.http.post(`v1.2/showcases/add`, showcase);
	}

	updateShowcase(
		id: string,
		showcase: Partial<{
			name: string;
			description: string;
			subtitle: string;
			attribution: string;
			submissions: string[];
		}>,
	): Promise<AxiosResponse> {
		return this.http.patch(`v1.2/showcases/${id}`, showcase);
	}

	changeShowcaseMode(data: ChangeShowcaseMode): Promise<AxiosResponse> {
		return this.http.post(`v1.2/showcases/changeAccessMode`, data);
	}

	deleteShowcase(id: string): Promise<AxiosResponse> {
		return this.http.delete(`v1.2/showcases/${id}`);
	}

	fetchLeaderboard(lessonId: string, classId: string): Promise<AxiosResponse> {
		return this.http.get(`v1.2/leaderboards/${lessonId}`, {
			params: {
				classId: classId,
			},
		});
	}

	resetAllLeaderboardScores(lessonId: string, classId: string): Promise<AxiosResponse> {
		return this.http.post(`v1.2/leaderboards/resetForClassAndLesson`, {
			classId: classId,
			lessonId: lessonId,
		});
	}

	resetStudentLeaderboardScore(lessonId: string, classId: string, studentId: string): Promise<AxiosResponse> {
		return this.http.post(`v1.2/leaderboards/resetForClassAndLesson`, {
			classId: classId,
			lessonId: lessonId,
			studentId: studentId,
		});
	}

	async inviteColleague(emails: string[]): Promise<InviteColleagueResponse | InviteColleagueError> {
		try {
			const response = await this.http.post('v1.2/teachers/inviteColleague', { emails: emails });
			return response.data as InviteColleagueResponse;
		} catch {
			return { reason: 'OTHER' };
		}
	}

	async getTeacherReferralLink(): Promise<TeacherReferralLinkResponse | TeacherReferralLinkError> {
		try {
			const response = await this.http.get('v1.2/teachers/referralLink');
			return response.data as TeacherReferralLinkResponse;
		} catch {
			return { reason: 'OTHER' };
		}
	}

	async resolveShortLink(code: string): Promise<ResolveShortLinkResponse | ResolveShortLinkError> {
		try {
			const response = await this.http.get(`v1.2/short-links/${encodeURIComponent(code)}`);
			return response.data as ResolveShortLinkResponse;
		} catch (error) {
			const errorCode = (error as AxiosError<GenericErrorModel | undefined>).response?.data?.code;
			if (errorCode === 'SHORT_LINK_INVALID') {
				return { reason: 'SHORT_LINK_INVALID' };
			}
			return { reason: 'OTHER' };
		}
	}

	async classTeachers(classId: string): Promise<ClassTeachersResponse | ClassTeachersError> {
		try {
			const response = await this.http.get(`v1.2/classes/${classId}/teachers`);
			return response.data as ClassTeachersResponse;
		} catch {
			return { reason: 'OTHER' };
		}
	}

	async inviteCoTeacher(classId: string, email: string): Promise<ClassTeachersResponse | InviteCoTeacherError> {
		try {
			const response = await this.http.post(`v1.2/classes/${classId}/teachers`, { email: email });
			return response.data as ClassTeachersResponse;
		} catch (error) {
			const code = (error as AxiosError<GenericErrorModel | undefined>).response?.data?.code;
			if (code === 'INVALID_EMAIL' || code === 'ALREADY_COTEACHER' || code === 'OWNER_EMAIL') {
				return { reason: code };
			}
			return { reason: 'OTHER' };
		}
	}

	async removeCoTeacher(classId: string, email: string): Promise<ClassTeachersResponse | ClassTeachersError> {
		try {
			const response = await this.http.delete(`v1.2/classes/${classId}/teachers`, { data: { email: email } });
			return response.data as ClassTeachersResponse;
		} catch {
			return { reason: 'OTHER' };
		}
	}

	async coTeacherInvitations(): Promise<CoTeacherInvitationsResponse | { reason: 'OTHER' }> {
		try {
			const response = await this.http.get('v1.2/classes/co-teacher-invitations');
			return response.data as CoTeacherInvitationsResponse;
		} catch {
			return { reason: 'OTHER' };
		}
	}

	async acceptCoTeacherInvite(classId: string): Promise<ClassTeachersResponse | CoTeacherInviteActionError> {
		try {
			const response = await this.http.post(`v1.2/classes/${classId}/teachers/accept`, {});
			return response.data as ClassTeachersResponse;
		} catch (error) {
			const code = (error as AxiosError<GenericErrorModel | undefined>).response?.data?.code;
			if (code === 'COTEACHER_INVITE_NOT_FOUND') {
				return { reason: 'NOT_FOUND' };
			}
			return { reason: 'OTHER' };
		}
	}

	async declineCoTeacherInvite(classId: string): Promise<{ status: 'DECLINED' } | CoTeacherInviteActionError> {
		try {
			const response = await this.http.post(`v1.2/classes/${classId}/teachers/decline`, {});
			return response.data as { status: 'DECLINED' };
		} catch (error) {
			const code = (error as AxiosError<GenericErrorModel | undefined>).response?.data?.code;
			if (code === 'COTEACHER_INVITE_NOT_FOUND') {
				return { reason: 'NOT_FOUND' };
			}
			return { reason: 'OTHER' };
		}
	}

	private convertResponse = <T>(response: AxiosResponse): T => {
		return response.data as T;
	};

	private convertError = (error: unknown): HttpErrorResult => {
		let errorModel: ErrorModel;
		let statusCode: number = -1;

		if (axios.isAxiosError(error)) {
			const axiosError = error as AxiosError;
			try {
				errorModel = axiosError.response?.data as ErrorModel;
				statusCode = error.response?.status ?? -1;
			} catch {
				errorModel = {
					code: ErrorCode.UnknownError,
					messages: ['Something went wrong, please try again later!'],
				};
			}
		} else {
			errorModel = {
				code: ErrorCode.UnknownError,
				messages: ['Something went wrong, please try again later!'],
			};
		}

		return {
			status: statusCode,
			errorModel: errorModel,
		};
	};
}

export {
	ApiClient,
	type ErrorModel,
	type HttpErrorResult,
	type NewClassType,
	type PlanDetails,
	type KycDetails,
	type KycProfile,
	type TemporaryData,
	type StudentClassImportResponse,
	type EvaluationType,
};
